import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, PenTool, X, AlertCircle } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import { colors, fonts } from "../../styles/theme";
import { Button } from "../ui";
import FieldToolbar from "./FieldToolbar";
import PdfPageViewer from "./PdfPageViewer";
import { convertFieldsToSignwell } from "./coordinateUtils";

// Configure pdf.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

export default function SignatureFieldEditor({
  pdfUrl,
  documentName,
  recipientEmail,
  recipientName,
  onClose,
  onSend,
}) {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState([]);
  const [sending, setSending] = useState(false);

  // Counter-sign state
  const [counterSign, setCounterSign] = useState(false);
  const [counterEmail, setCounterEmail] = useState("");
  const [counterName, setCounterName] = useState("");

  // Active signer (0 = recipient, or 0 = counter-signer if enabled)
  const [activeSigner, setActiveSigner] = useState(0);

  // Page dimensions for coordinate conversion
  const pageDimsRef = useRef(new Map());

  // Container width for responsive sizing
  const scrollRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(700);

  // Build signers list
  const signers = [];
  if (counterSign && counterEmail) {
    signers.push({
      label: counterName || "You (counter-signer)",
      email: counterEmail,
      color: "#8b5cf6",
    });
  }
  signers.push({
    label: recipientName || recipientEmail,
    email: recipientEmail,
    color: "#3b82f6",
  });

  // Load PDF
  useEffect(() => {
    if (!pdfUrl) return;
    setLoading(true);
    setError(null);

    pdfjsLib.getDocument({ url: pdfUrl, cMapUrl: undefined }).promise
      .then(async (doc) => {
        setPdfDoc(doc);
        const pageList = [];
        for (let i = 1; i <= doc.numPages; i++) {
          pageList.push(await doc.getPage(i));
        }
        setPages(pageList);
        setLoading(false);
      })
      .catch((err) => {
        console.error("PDF load failed:", err);
        setError("Failed to load PDF preview. The document will still be sent to SignWell.");
        setLoading(false);
      });
  }, [pdfUrl]);

  // Measure container width on mount and resize
  useEffect(() => {
    const measure = () => {
      if (scrollRef.current) {
        setContainerWidth(scrollRef.current.clientWidth);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const handlePageDimensions = useCallback((pageNum, dims) => {
    pageDimsRef.current.set(pageNum, dims);
  }, []);

  const handleAddField = useCallback((field) => {
    setFields((prev) => [...prev, field]);
  }, []);

  const handleMoveField = useCallback((id, x, y) => {
    setFields((prev) => prev.map((f) => f.id === id ? { ...f, x, y } : f));
  }, []);

  const handleResizeField = useCallback((id, w, h) => {
    setFields((prev) => prev.map((f) => f.id === id ? { ...f, width: w, height: h } : f));
  }, []);

  const handleDeleteField = useCallback((id) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleSend = async () => {
    if (fields.length === 0) {
      if (!window.confirm("No signature fields placed. Send without field placement? (SignWell will prompt the signer to place fields.)")) {
        return;
      }
    }

    setSending(true);
    try {
      const swFields = convertFieldsToSignwell(fields, pageDimsRef.current);
      await onSend({
        fields: swFields,
        counterSign,
        counterSignerEmail: counterSign ? counterEmail : undefined,
        counterSignerName: counterSign ? counterName : undefined,
      });
    } catch (err) {
      alert(`Failed to send: ${err.message}`);
      setSending(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.7)", display: "flex",
    }}>
      {/* Left sidebar */}
      <div style={{
        display: "flex", flexDirection: "column",
        background: colors.bgCard, borderRight: `1px solid ${colors.border}`,
        width: 240, flexShrink: 0,
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 14px", borderBottom: `1px solid ${colors.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <h3 style={{
              fontFamily: fonts.heading, fontSize: 14, color: colors.text,
              margin: 0, lineHeight: 1.3,
            }}>
              Place Signature Fields
            </h3>
            <div style={{
              fontSize: 11, color: colors.textSecondary, marginTop: 2,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              maxWidth: 180,
            }}>
              {documentName}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: 4, color: colors.textSecondary,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Counter-sign toggle */}
        <div style={{ padding: "12px 14px", borderBottom: `1px solid ${colors.border}` }}>
          <label style={{
            display: "flex", alignItems: "center", gap: 8,
            fontSize: 12, color: colors.text, cursor: "pointer",
          }}>
            <input
              type="checkbox"
              checked={counterSign}
              onChange={(e) => {
                setCounterSign(e.target.checked);
                if (!e.target.checked) setActiveSigner(0);
              }}
              style={{ accentColor: colors.accent }}
            />
            Counter-sign
          </label>
          {counterSign && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
              <input
                type="email"
                value={counterEmail}
                onChange={(e) => setCounterEmail(e.target.value)}
                placeholder="Your email"
                style={{
                  padding: "6px 8px", fontSize: 12, fontFamily: fonts.body,
                  border: `1px solid ${colors.border}`, borderRadius: 5,
                  background: colors.bg, color: colors.text, outline: "none",
                }}
              />
              <input
                type="text"
                value={counterName}
                onChange={(e) => setCounterName(e.target.value)}
                placeholder="Your name"
                style={{
                  padding: "6px 8px", fontSize: 12, fontFamily: fonts.body,
                  border: `1px solid ${colors.border}`, borderRadius: 5,
                  background: colors.bg, color: colors.text, outline: "none",
                }}
              />
            </div>
          )}
        </div>

        {/* Field toolbar */}
        <FieldToolbar
          signers={signers}
          activeSigner={activeSigner}
          onActiveSigner={setActiveSigner}
        />

        {/* Bottom action bar */}
        <div style={{
          padding: "14px", borderTop: `1px solid ${colors.border}`,
          marginTop: "auto",
        }}>
          <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 10 }}>
            {fields.length} field{fields.length !== 1 ? "s" : ""} placed
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSend}
            disabled={sending || (counterSign && !counterEmail)}
            style={{ width: "100%" }}
          >
            {sending ? (
              <><Loader2 size={13} className="spin" /> Sending...</>
            ) : (
              <><PenTool size={13} /> Send for Signature</>
            )}
          </Button>
        </div>
      </div>

      {/* PDF viewer area */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, overflow: "auto", padding: 20,
          background: "#1a1a2e",
        }}
      >
        {loading && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", height: "100%", gap: 12,
          }}>
            <Loader2 size={32} color="#fff" className="spin" />
            <span style={{ color: "#aaa", fontSize: 13 }}>Loading PDF...</span>
          </div>
        )}

        {error && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", height: "100%", gap: 12,
          }}>
            <AlertCircle size={32} color="#f59e0b" />
            <span style={{ color: "#ddd", fontSize: 13, textAlign: "center", maxWidth: 400 }}>
              {error}
            </span>
            <Button variant="primary" size="sm" onClick={handleSend} disabled={sending}>
              Send Without Field Placement
            </Button>
          </div>
        )}

        {!loading && !error && pages.map((page, i) => (
          <PdfPageViewer
            key={i + 1}
            page={page}
            pageNum={i + 1}
            fields={fields}
            signers={signers}
            activeSigner={activeSigner}
            onAddField={handleAddField}
            onMoveField={handleMoveField}
            onResizeField={handleResizeField}
            onDeleteField={handleDeleteField}
            onPageDimensions={handlePageDimensions}
            containerWidth={containerWidth}
          />
        ))}
      </div>    </div>
  );
}
